import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { Box, Button, Dialog, IconButton, List, ListItem, Typography } from '@material-ui/core';
import { Error } from '@pec/aion-ui-components/components/Error';
import { Loading } from '@pec/aion-ui-components/components/Loading';
import { fetchOrganizationInfo } from '@pec/aion-ui-core/slices/organizationInfo';
import { fetchMainSubscription } from '@pec/aion-ui-core/slices/mainSubscription';
import { useAppDispatch, useTypedSelector } from 'combineReducers';
import { PRODUCTS_LOGOS } from 'helpers/newDashboard';
import { handleCompletion } from 'helpers/activateBusinessShield';
import { fetchThirdPartyProducts } from 'slices/thirdPartyProducts';

export const BUSINESS_SHIELD_STORAGE_KEY = 'businessShield:paymentIsProcessing';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      margin: theme.spacing(2, 3),
      display: 'flex',
      flexDirection: 'column',
      gap: `${theme.spacing(3)}px`
    },
    header: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: `${theme.spacing(2)}px`
    },
    logo: {
      height: 70,
      width: 70
    },
    title: {
      fontWeight: theme.typography.fontWeightMedium
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: `${theme.spacing(2)}px`
    },
    listItem: {
      lineHeight: '20px',
      letterSpacing: 0.25,
      padding: 0,
      paddingTop: 1,
      paddingBottom: 1
    },
    icon: {
      color: 'var(--green)',
      marginRight: 5,
      height: 16,
      width: 16
    },
    button: {
      letterSpacing: 1.25
    },
    dialogClose: {
      position: 'absolute',
      top: 8,
      right: 8
    }
  })
);

type RouteParams = {
  organizationId: string;
};

export const BusinessShieldContainer: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation('subscriber');
  const Logo = PRODUCTS_LOGOS.businessShield;
  const { organizationId } = useParams<RouteParams>();
  const dispatch = useAppDispatch();
  const { userInfo } = useTypedSelector(state => state.userInfo);
  const { mainSubscription } = useTypedSelector(state => state.mainSubscription);
  const { organizationInfo, isFetching: isFetchingOrganizationInfo } = useTypedSelector(
    state => state.organizationInfo
  );
  const { thirdPartyProducts, isFetching: isFetchingProducts } = useTypedSelector(state => state.thirdPartyProducts);
  const [formState, setFormState] = React.useState<{ isLoading: boolean; error?: string }>({ isLoading: false });
  const isFetching = isFetchingOrganizationInfo || isFetchingProducts;

  React.useEffect(() => {
    setFormState({ isLoading: true });
    if (organizationId) {
      dispatch(fetchOrganizationInfo(organizationId));
      dispatch(fetchMainSubscription(organizationId));
    }
  }, [dispatch, organizationId]);

  React.useEffect(() => {
    const { countryISO2 } = mainSubscription;

    if (organizationInfo && countryISO2) {
      dispatch(
        fetchThirdPartyProducts({ organizationInfo, countryISO2, afterEvery: () => setFormState({ isLoading: false }) })
      );
    }
  }, [dispatch, organizationInfo, mainSubscription]);

  const onDialogClose = () => {
    setFormState({ isLoading: false });
  };

  const handleActivateNow = async () => {
    setFormState({ isLoading: true });
    if (organizationInfo && userInfo && mainSubscription) {
      try {
        const productSelect =
          thirdPartyProducts.products.find(product => product.subscriptionTier === 'Business Shield')?.id ?? '';
        const payload = {
          organizationInfo,
          taxRate: thirdPartyProducts.taxRate,
          productSelect,
          userInfo,
          mainSubscription
        };
        const { data } = await handleCompletion(payload);
        localStorage.setItem(BUSINESS_SHIELD_STORAGE_KEY, 'true');
        window.location.href = data.checkoutUrl;
      } catch (error) {
        setFormState({ isLoading: false, error: t('businessShield.error', 'Unknown error, please try again later.') });
      }
    }
  };

  if (formState.isLoading || isFetching) {
    return (
      <Box width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
        <Loading />
      </Box>
    );
  }

  const FEATURES = [
    t(
      'businessShield.features.comprehensiveLibrary',
      'Comprehensive library of step-by-step procedures across H&S, environment, HR and employment'
    ),
    t('businessShield.features.modelPolicies', 'Model policies that can be customised to suit your requirements'),
    t('businessShield.features.sampleForms', 'Sample forms and letters'),
    t('businessShield.features.inPersonHelpline', 'In person helpline available Monday - Friday 08:00 - 17:00'),
    t('businessShield.features.accessToResponseLine', 'Access to a 24-hour response line for H&S questions'),
    t('businessShield.features.dailyNews', 'Daily news and legislation updates'),
    t(
      'businessShield.features.aDedicatedCHASComplienceSection',
      'A dedicated CHAS compliance section with useful documentation that will assist with gaining certification'
    )
  ];
  const BENEFITS = [
    t(
      'businessShield.benefits.helpsToComply',
      'Helps you to comply the Management of Health and Safety at Work Regulation by giving access to competent H&S advice'
    ),
    t(
      'businessShield.benefits.guides',
      'Guides you through the legislative landscape on H&S, environment, HR and employment'
    )
  ];

  const renderListItem = (item: string) => (
    <ListItem key={item} classes={{ root: classes.listItem }}>
      <CheckIcon classes={{ root: classes.icon }} />
      {item}
    </ListItem>
  );

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Logo className={classes.logo} />
        <Typography variant="h6" className={classes.title}>
          {t('businessShield.title', 'Business Shield - Free expert advice')}
        </Typography>
      </Box>
      <Box className={classes.content}>
        <Box>
          <Typography variant="body2">{t('businessShield.features.title', 'Features')}</Typography>
          <List disablePadding>{FEATURES.map(renderListItem)}</List>
        </Box>
        <Box>
          <Typography variant="body2">{t('businessShield.benefits.title', 'Benefits')}</Typography>
          <List disablePadding>{BENEFITS.map(renderListItem)}</List>
        </Box>
      </Box>
      <Box>
        <Button
          color="primary"
          variant="contained"
          disabled={formState.isLoading}
          classes={{ root: classes.button }}
          onClick={handleActivateNow}
        >
          {t('businessShield.activateNow', 'ACTIVATE NOW')}
        </Button>
      </Box>

      <Dialog open={!!formState.error} onClose={onDialogClose}>
        <IconButton className={classes.dialogClose} size="small" onClick={onDialogClose}>
          <CloseIcon />
        </IconButton>
        <Error message={formState.error} />
      </Dialog>
    </Box>
  );
};
